import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-voiceagent');

/**
 * Converts an ArrayBuffer to a base64 string.
 * @param buffer The ArrayBuffer to convert.
 * @returns A base64 encoded string.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
  
  /**
   * Converts a base64 string to a Float32Array.
   * Assumes the base64 string represents 16-bit PCM audio.
   * @param base64String The base64 encoded audio string.
   * @returns A Float32Array representing the audio.
   */
  export function base64ToFloat32Array(base64String: string): Float32Array {
    try {
      const binaryString = window.atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
  
      // Assuming the byte array is 16-bit PCM
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);
  
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0; // Normalize to [-1.0, 1.0]
      }
      return float32Array;
    } catch (error) {
      logger.error('Error converting base64 to Float32Array:', error);
      throw error;
    }
  }
  
  /**
   * Converts Float32Array audio data to 16-bit PCM Int16Array.
   * This is used for processing microphone input before sending.
   * @param input Float32Array from the audio buffer.
   * @param samplingRatio Ratio for downsampling if original sample rate is higher than target.
   * @returns Int16Array containing 16-bit PCM data.
   */
  export function float32To16BitPCM(input: Float32Array, samplingRatio: number = 1): Int16Array {
    const numSamples = Math.round(input.length / samplingRatio);
    const pcmOutput = new Int16Array(numSamples);
    let SMax = 0;
    for (let i = 0; i < input.length; i++) {
      if (Math.abs(input[i]) > SMax) {
        SMax = Math.abs(input[i]);
      }
    }
    const gain = SMax > 0 ? 1/SMax : 1;
  
    for (let i = 0; i < numSamples; i++) {
      const sampleIndex = Math.floor(i * samplingRatio);
      let sample = input[sampleIndex];
      // Apply gain and clip to range [-1, 1]
      sample = Math.max(-1, Math.min(1, sample * gain));
      // Convert to 16-bit integer
      pcmOutput[i] = sample * 0x7FFF;
    }
    return pcmOutput;
  }
  
  /**
   * Checks if the current browser is Firefox.
   * @returns True if Firefox, false otherwise.
   */
  export function isFirefox(): boolean {
    return navigator.userAgent.toLowerCase().includes('firefox');
  }
  
  export const TARGET_SAMPLE_RATE = 16000;
  